import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * What the customer panel and the champ panel share: the parameter plumbing.
 *
 * Each panel lives in the URL (`?customer=<id>&ctab=…`, `?champ=<id>&htab=…`)
 * so it survives a reload, can be shared, and Back closes it. **Only one
 * profile panel is open at a time**: opening one deletes the other's two
 * parameters in the same update — as a new history entry, so Back from a champ
 * opened inside a customer's panel returns to that customer.
 *
 * Opening pushes a history entry; closing, choosing a tab and stepping through
 * a list replace it.
 */
export interface ProfileParamNames {
  param: string;
  tabParam: string;
}

/** The two profile panels' URL parameters, in one place so neither imports the other. */
export const CUSTOMER_PANEL_PARAMS: ProfileParamNames = {
  param: "customer",
  tabParam: "ctab",
};
export const CHAMP_PANEL_PARAMS: ProfileParamNames = {
  param: "champ",
  tabParam: "htab",
};

/**
 * The tab of a full profile PAGE (`/customers/:id`, `/champs/:id`). It is not
 * `ctab` / `htab`: those belong to a panel opened over the page, and sharing
 * them let the panel delete or move the page's own tab.
 */
export const PAGE_TAB_PARAM = "ptab";

export function parsePositiveId(value: string | null | undefined): number | null {
  if (!value || !/^\d+$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/** What had focus when a panel opened, so Close can give it back. */
let opener: HTMLElement | null = null;

/** Marks a profile sheet's content, so a panel opened from inside one keeps the first opener. */
export const PROFILE_SHEET_ATTRIBUTE = "data-profile-sheet";

export function rememberOpener(): void {
  const active = document.activeElement;
  // Opened from a link inside the other profile panel: keep what opened that one.
  if (
    active instanceof HTMLElement &&
    active.closest(`[${PROFILE_SHEET_ATTRIBUTE}]`) &&
    opener?.isConnected
  ) {
    return;
  }
  opener = active instanceof HTMLElement && active !== document.body ? active : null;
}

/** Focus goes back to whatever opened the panel, if it is still on the page. */
export function restoreOpener(): boolean {
  const target = opener;
  opener = null;
  if (target?.isConnected) {
    target.focus();
    return true;
  }
  return false;
}

/** Whether the URL holds this profile parameter right now (read outside React state). */
export function urlHasParam(param: string): boolean {
  return new URLSearchParams(window.location.search).has(param);
}

export function useProfilePanelParams<Tab extends string>(
  own: ProfileParamNames,
  rival: ProfileParamNames,
  parseTab: (value: string | null) => Tab | null,
) {
  const [params, setParams] = useSearchParams();
  const id = parsePositiveId(params.get(own.param));
  const tab = parseTab(params.get(own.tabParam));

  const open = useCallback(
    (nextId: number, options?: { tab?: Tab; replace?: boolean }) => {
      if (!options?.replace) rememberOpener();
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.set(own.param, String(nextId));
          if (options?.tab) updated.set(own.tabParam, options.tab);
          else updated.delete(own.tabParam);
          // One profile panel at a time.
          updated.delete(rival.param);
          updated.delete(rival.tabParam);
          return updated;
        },
        { replace: options?.replace ?? false },
      );
    },
    [setParams, own.param, own.tabParam, rival.param, rival.tabParam],
  );

  const close = useCallback(
    () =>
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.delete(own.param);
          updated.delete(own.tabParam);
          return updated;
        },
        { replace: true },
      ),
    [setParams, own.param, own.tabParam],
  );

  return { id, tab, open, close };
}

/** The tab on a full page (`/customers/:id`, `/champs/:id`), which has no panel parameter. */
export function useProfileTabParam<Tab extends string>(
  tabParam: string,
  parseTab: (value: string | null) => Tab | null,
) {
  const [params, setParams] = useSearchParams();
  const tab = parseTab(params.get(tabParam));
  const setTab = useCallback(
    (next: Tab) =>
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          if (next === "overview") updated.delete(tabParam);
          else updated.set(tabParam, next);
          return updated;
        },
        { replace: true },
      ),
    [setParams, tabParam],
  );
  return { tab, setTab };
}
